import React, { useState, useCallback, useEffect } from 'react';
import { signInWithPopup, signOut } from 'firebase/auth';
import { Routes, Route } from 'react-router-dom';
import { auth, googleProvider, microsoftProvider } from './authConfig';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import Navbar from './components/common/Navbar';
import Sidebar from './components/common/Sidebar';
import ErrorBoundary from './components/common/ErrorBoundary';
import AccountOverlay from './components/common/AccountOverlay';
import JiraAccountManager from './components/JiraAccountManager';
import AdminPanel from './components/AdminPanel';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingDots from './components/common/LoadingDots';
import ProjectsView from './components/ProjectsView';
import DevCapacity from './components/DevCapacity';
import PlanOverview from './components/PlanOverview';
import ProjectPlan from './components/ProjectPlan';
import CapacityOverview from './components/CapacityOverview';
import InvitationsPage from './components/InvitationsPage';
import ApiTokens from './components/ApiTokens';
import TeamView from './components/TeamView';
// Context
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { UserProvider, useUser } from './context/UserContext';
import { ProjectsProvider } from './context/ProjectsContext';

function AppContent() {


  const {
    isAuthenticated,
    user, setUser,
    activeJiraAccount, setActiveJiraAccount,
    loading, setLoading,
    error, setError,
    jiraAccountStatus, setJiraAccountStatus
  } = useUser();

  const { isDarkMode } = useTheme();

  // Overlay States
  const [showAccountOverlay, setShowAccountOverlay] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showApiTokens, setShowApiTokens] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);


  // Sidebar collapse state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Close mobile sidebar when screen width is greater than 640px
  const desktopMediaQuery = window.matchMedia?.('(min-width: 768px)');

  useEffect(() => {
    if (!desktopMediaQuery) return;
    const handler = () => {
      if (desktopMediaQuery.matches) setMobileSidebarOpen(false);
    };
    desktopMediaQuery.addEventListener('change', handler);
    return () => desktopMediaQuery.removeEventListener('change', handler);
  }, [desktopMediaQuery]);

  const handleGoogleLogin = useCallback(async () => {
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Google login failed:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [setLoading, setError]);

  const handleMicrosoftLogin = useCallback(async () => {
    setLoading(true);
    try {
      await signInWithPopup(auth, microsoftProvider);
    } catch (err) {
      console.error('Microsoft login failed:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [setLoading, setError]);


  const handleLogout = async () => {
    setLoading(true);
    await signOut(auth);
  };

  if (loading) {
    return <LoadingDots message="Loading..." />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <LoginScreen
          onGoogleLogin={handleGoogleLogin}
          onMicrosoftLogin={handleMicrosoftLogin}
          error={error}
          loading={loading}
        />
        <ToastContainer position="bottom-right" autoClose={3000} />
      </>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-slate-100 dark:bg-slate-900 transition-colors duration-300 flex">
      <Sidebar
        mobileDrawerOpen={mobileSidebarOpen}
        onMobileDrawerClose={() => setMobileSidebarOpen(false)}
        onShowAdminPanel={() => setShowAdminPanel(true)}
        onShowAccountOverlay={() => setShowAccountOverlay(true)}
        onShowApiTokens={() => setShowApiTokens(true)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Navbar
          onLogout={handleLogout}
          mobileSidebarOpen={mobileSidebarOpen}
          sidebarCollapsed={sidebarCollapsed}
          onMobileSidebarToggle={() => {
            if (desktopMediaQuery?.matches) {
              setSidebarCollapsed((v) => !v);
            } else {
              setMobileSidebarOpen((v) => !v);
            }
          }}
        />

        <main className="flex-1 overflow-y-auto min-h-0">
          <Routes>
            <Route
              path="/"
              element={
                <Dashboard />
              }
            />
            <Route
              path="/profile"
              element={
                <Profile
                  onLogout={handleLogout}
                  onUserUpdate={(updatedUser) => setUser(prev => ({ ...prev, ...updatedUser }))}
                />
              }
            />
            <Route
              path="/projects"
              element={
                <ProjectsView />
              }
            />
            <Route
              path="/team"
              element={<TeamView />}
            />
            <Route
              path="/projects/plan"
              element={<PlanOverview />}
            />
            <Route
              path="/projects/capacity"
              element={<CapacityOverview />}
            />
            <Route path="/projects/:id/plan"
              element={<ProjectPlan />}
            />
            <Route
              path="/projects/:id/capacity"
              element={
                <DevCapacity />
              }
            />
            <Route
              path="/invitations"
              element={<InvitationsPage />}
            />
          </Routes>
        </main>
      </div>

      {/* Overlays */}
      {showAccountOverlay && (
        <AccountOverlay onClose={() => setShowAccountOverlay(false)}>
          <JiraAccountManager
            activeJiraAccount={activeJiraAccount}
            onJiraAccountChange={setActiveJiraAccount}
            onAccountStatusChange={setJiraAccountStatus}
            onClose={() => setShowAccountOverlay(false)}
          />
        </AccountOverlay>
      )}

      {showAdminPanel && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5"
          onClick={() => setShowAdminPanel(false)}
        >
          <div
            className="rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative bg-white dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <AdminPanel onClose={() => setShowAdminPanel(false)} />
          </div>
        </div>
      )}
      {showApiTokens && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5"
          onClick={() => setShowApiTokens(false)}
        >
          <div
            className="rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative bg-white dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 flex justify-end p-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setShowApiTokens(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm"
              >
                ✕ Close
              </button>
            </div>
            <ApiTokens />
          </div>
        </div>
      )}
      <ToastContainer theme={isDarkMode ? 'dark' : 'light'} position="bottom-right" autoClose={3000} />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <UserProvider>
          <ProjectsProvider>
            <AppContent />
          </ProjectsProvider>
        </UserProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;